import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthCard, Button, LoadingSpinner } from '../../components';
import api from '../../utils/api';

/**
 * ADR-012 Phase 1 agent email verification confirm page.
 *
 * The confirmation link Klyx sends via Resend (see EmailService's
 * sendAgentEmailVerification) opens this page. We call the backend's
 * public confirm endpoint from here and render a branded success /
 * error page instead of dumping the API's raw HTML at the recipient.
 *
 * Distinct from /verify-email (which is the HUMAN Klyx-account
 * verification): that flow POSTs /auth/verify-email to activate the
 * account, this one GETs /api/agents/verify/email/confirm to prove
 * the agent's owner controls the email address. Different targets,
 * different auth semantics, different post-success actions.
 */
const AgentVerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    // Deliberately fire the confirm on every mount even in strict
    // mode. The backend's markVerified is idempotent-adjacent:
    // second call on an already-verified row returns 400 (invalid
    // pending row) which surfaces here as "expired or already used"
    // — matches reality, no false failure.
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token in this link.');
      return;
    }

    let cancelled = false;
    api
      .get(`/agents/verify/email/confirm?token=${encodeURIComponent(token)}`)
      .then(() => {
        if (cancelled) return;
        setStatus('success');
        setMessage(
          'The email address is now verified for this agent. You can close this window.',
        );
      })
      .catch(() => {
        if (cancelled) return;
        // The GET endpoint returns HTML for any status; axios's error
        // path fires on 4xx/5xx. We don't try to parse the response
        // body — the failure modes (expired, already used, unknown
        // token) collapse to the same user-facing message.
        setStatus('error');
        setMessage(
          'This verification link is invalid, already used, or has expired. Ask the agent owner to send a new one.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthCard
      title={
        status === 'loading'
          ? 'Verifying email…'
          : status === 'success'
            ? 'Email verified'
            : 'Verification failed'
      }
      subtitle={status === 'loading' ? 'Please wait' : undefined}
    >
      <div className="verify-email-content">
        {status === 'loading' && (
          <LoadingSpinner size="medium" message="Confirming with Klyx…" />
        )}

        {status === 'success' && (
          <div className="success-message">
            <svg
              className="success-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>{message}</p>
            <Link to="/" className="back-link">
              <Button variant="secondary" fullWidth>
                Return to Klyx
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="error-message">
            <svg
              className="error-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>{message}</p>
            <Link to="/" className="back-link">
              <Button variant="secondary" fullWidth>
                Return to Klyx
              </Button>
            </Link>
          </div>
        )}
      </div>
    </AuthCard>
  );
};

export default AgentVerifyEmail;
